// src/components/AppRouter.tsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

// Import de vos composants existants
import AdminDashboard from './admin/AdminDashboard';
import AuditorInterface from './audit/AuditorInterface';
import LoginPage from './auth/LoginPage';
import AuditorDashboard from './auditor/AuditorDashboard';
import AuditResultsInterface from './audit/AuditResultsInterface';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'auditor';
}

// Composant de protection des routes
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-600 rounded-full animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
          </div>
          <p className="text-xl font-semibold text-gray-700">Vérification des permissions...</p>
          <p className="text-gray-500">Chargement de votre espace de travail</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return <LoginPage />;
  }

  if (!profile.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-red-100">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Compte désactivé</h2>
            <p className="text-gray-600 mb-6">
              Votre compte a été désactivé. Veuillez contacter un administrateur pour le réactiver.
            </p>
            <div className="bg-red-50 rounded-xl p-4 border border-red-100">
              <p className="text-sm text-red-700">
                <strong>Contact:</strong> admin@focal.com
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              Actualiser la page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRole && profile.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé</h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les permissions nécessaires pour accéder à cette section.
            </p>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Votre rôle:</strong> {profile.role === 'admin' ? '👑 Administrateur' : '🔍 Auditeur'}
              </p>
              <p className="text-sm text-blue-600 mt-1">
                <strong>Requis:</strong> {requiredRole === 'admin' ? '👑 Administrateur' : '🔍 Auditeur'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// ✅ Composant de routage principal avec route publique
const SimpleRouter: React.FC = () => {
  const [currentPath, setCurrentPath] = useState(() => {
    return window.location.pathname;
  });

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ✅ Route PUBLIQUE pour /audit-results - SANS authentification
  if (currentPath === '/audit-results') {
    return <PublicAuditResultsInterface />;
  }

  // ✅ Routes protégées pour le reste de l'app
  return <MainAppRouter />;
};

// ✅ Composant public pour les résultats d'audit
const PublicAuditResultsInterface: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header public */}
      <div className="bg-white shadow-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Focal Systems - Audit Results
              </h1>
              <p className="text-lg text-gray-600 mt-1">Quality assurance dashboard - Public view</p>
            </div>
            <div className="text-sm text-gray-500">
              🔓 Public Access
            </div>
          </div>
        </div>
      </div>
      
      {/* Composant des résultats d'audit */}
      <AuditResultsInterface />
    </div>
  );
};

// Composant principal avec routage pour auditeur (sans boutons)
const AuditorApp: React.FC = () => {
  // ✅ Initialiser l'état depuis localStorage pour persistance
  const [currentView, setCurrentView] = useState<'dashboard' | 'audit'>(() => {
    try {
      const savedState = localStorage.getItem('audit_view_state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Vérifier que l'état n'est pas trop ancien (24h)
        const savedTime = new Date(parsed.timestamp).getTime();
        const now = new Date().getTime();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        
        if (hoursDiff < 24 && parsed.currentView) {
          console.log('🔄 État restauré depuis localStorage:', parsed.currentView);
          return parsed.currentView;
        }
      }
    } catch (error) {
      console.warn('⚠️ Erreur lecture localStorage:', error);
    }
    return 'dashboard';
  });

  const [auditSessionActive, setAuditSessionActive] = useState<boolean>(() => {
    try {
      const savedState = localStorage.getItem('audit_view_state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return parsed.sessionActive || false;
      }
    } catch (error) {
      console.warn('⚠️ Erreur lecture sessionActive:', error);
    }
    return false;
  });

  const handleStartAudit = () => {
    setCurrentView('audit');
    setAuditSessionActive(false);
    
    // ✅ Sauvegarder l'état
    localStorage.setItem('audit_view_state', JSON.stringify({
      currentView: 'audit',
      sessionActive: false,
      timestamp: new Date().toISOString()
    }));
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setAuditSessionActive(false);
    
    // ✅ Nettoyer localStorage
    localStorage.removeItem('audit_view_state');
  };

  const handleSessionStateChange = (isActive: boolean) => {
    setAuditSessionActive(isActive);
    
    // ✅ Mettre à jour localStorage
    if (currentView === 'audit') {
      localStorage.setItem('audit_view_state', JSON.stringify({
        currentView: 'audit',
        sessionActive: isActive,
        timestamp: new Date().toISOString()
      }));
    }
  };

  // ✅ Nettoyer localStorage au démontage
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Garder l'état même après fermeture navigateur pour persistance complète
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (currentView === 'audit') {
    return (
      <>
        {/* ✅ Bouton retour seulement si pas de session active */}
        {!auditSessionActive && (
          <div className="fixed top-4 left-4 z-30">
            <button
              onClick={handleBackToDashboard}
              className="bg-white/90 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-xl shadow-lg hover:bg-white transition-all duration-200 flex items-center space-x-2 border border-gray-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">← Dashboard</span>
            </button>
          </div>
        )}
        <AuditorInterface 
          onBackToDashboard={handleBackToDashboard}
          onSessionStateChange={handleSessionStateChange}
        />
      </>
    );
  }

  // ✅ Pas de boutons - juste le dashboard normal
  return <AuditorDashboard onStartAudit={handleStartAudit} />;
};

// Composant principal avec routage protégé
const MainAppRouter: React.FC = () => {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-purple-600 rounded-full animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Focal Audit Pro</h2>
          <p className="text-lg text-gray-600 mb-4">Chargement de l'application...</p>
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Vérification de l'authentification</span>
          </div>
        </div>
      </div>
    );
  }

  // Si pas connecté, afficher la page de connexion
  if (!profile) {
    return <LoginPage />;
  }

  if (profile.role === 'admin') {
    return (
      <ProtectedRoute requiredRole="admin">
        <AdminDashboard />
      </ProtectedRoute>
    );
  }

  if (profile.role === 'auditor') {
    return (
      <ProtectedRoute requiredRole="auditor">
        <AuditorApp />
      </ProtectedRoute>
    );
  }

  // Rôle non reconnu
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-yellow-200">
        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Rôle non reconnu</h2>
          <p className="text-gray-600 mb-6">
            Votre rôle utilisateur n'est pas reconnu par le système. Veuillez contacter un administrateur.
          </p>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Rôle actuel:</strong> <span className="font-mono bg-yellow-100 px-2 py-1 rounded">{profile.role}</span>
            </p>
            <p className="text-sm text-yellow-700 mt-2">
              <strong>Rôles autorisés:</strong> admin, auditor
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              Actualiser la page
            </button>
            <p className="text-xs text-gray-500">
              Si le problème persiste, contactez: admin@focal.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ Export du routeur principal avec route publique
export default SimpleRouter;